import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { IBulletinFilterModel } from '@/types/admin/bulletins';
import { useResponsive } from '@/hooks/common/useResponsive';

export type RecipientType = IBulletinFilterModel['recipientType'];

interface BulletinRecipientTabsProps {
  activeRecipientType: RecipientType;
  onRecipientTypeChange: (recipientType: RecipientType) => void;
}

const recipientTabs = [
  { key: 'all' as RecipientType, label: 'All residents' },
  { key: 'current' as RecipientType, label: 'Current residents' },
  { key: 'future' as RecipientType, label: 'Future residents' },
  { key: 'notice' as RecipientType, label: 'Lease notice' },
];

export const BulletinRecipientTabs: React.FC<BulletinRecipientTabsProps> = ({
  activeRecipientType,
  onRecipientTypeChange,
}) => {
  const { layoutMode } = useResponsive();

  if (layoutMode === 'desktop') {
    return (
      <View style={[styles.container, styles.desktopContainer]}>
        <View style={styles.desktopRow}>
          {recipientTabs.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.desktopTab,
                activeRecipientType === tab.key && styles.desktopActiveTab,
              ]}
              onPress={() => onRecipientTypeChange(tab.key)}
              activeOpacity={0.85}
            >
              <Text
                style={[
                  styles.desktopTabText,
                  activeRecipientType === tab.key && styles.desktopActiveTabText,
                ]}
                numberOfLines={1}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {recipientTabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeRecipientType === tab.key && styles.activeTab,
            ]}
            onPress={() => onRecipientTypeChange(tab.key)}
          >
            <Text
              style={[
                styles.tabText,
                activeRecipientType === tab.key && styles.activeTabText,
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  desktopContainer: {
    width: '100%',
  },
  desktopRow: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: 12,
  },
  scrollContent: {
    paddingHorizontal: 4,
    gap: 8,
  },
  desktopTab: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 100,
  },
  desktopActiveTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  activeTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  desktopTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  desktopActiveTabText: {
    color: '#FFFFFF',
  },
  activeTabText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
